import React, { useEffect, useState } from 'react';

import { cn } from '../../../lib/utils';
import PortalLayout from '../PortalLayout';
import { usePortalPayouts } from '../../../services/portal';
import { TILE, StageBadge, SkeletonRow, EmptyState, ErrorState, Pager, money, fmtDate } from '../portalShared';

const VIEWS = [
  { key: '', label: 'All' },
  { key: 'owed', label: 'Owed' },
  { key: 'paid', label: 'Paid' },
];

// The partner's $250 ledger: totals strip, filter pills, cards on mobile /
// table on desktop.
const PortalPayouts = () => {
  const [view, setView] = useState('');
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, isFetching, refetch } = usePortalPayouts(view || undefined, page);

  const payouts = data?.payouts || [];
  const summary = data?.summary || { totalOwed: 0, totalPaid: 0 };
  const total = data?.total || 0;
  const limit = data?.limit || 25;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  // A shrinking list can strand the user on an empty later page — snap back.
  useEffect(() => {
    if (!isLoading && page > 1 && payouts.length === 0 && total > 0) {
      setPage(Math.min(page - 1, totalPages));
    }
  }, [isLoading, page, payouts.length, total, totalPages]);

  return (
    <PortalLayout>
      <div className="mb-4">
        <h1 className="text-xl font-bold text-white">Payouts</h1>
        <p className="mt-0.5 text-sm text-neutral-400">
          $250 for every referral who becomes a client and completes their claim.
        </p>
      </div>

      {/* Totals strip */}
      <div className="mb-4 grid grid-cols-2 gap-3">
        <div className={`${TILE} p-4`}>
          {isLoading ? (
            <div className="h-7 w-24 animate-pulse rounded-md bg-white/10" />
          ) : (
            <div className="text-2xl font-bold text-amber-300">{money(summary.totalOwed)}</div>
          )}
          <div className="mt-1 text-xs font-medium uppercase tracking-wide text-neutral-400">
            Owed to you
          </div>
        </div>
        <div className={`${TILE} p-4`}>
          {isLoading ? (
            <div className="h-7 w-24 animate-pulse rounded-md bg-white/10" />
          ) : (
            <div className="text-2xl font-bold text-emerald-300">{money(summary.totalPaid)}</div>
          )}
          <div className="mt-1 text-xs font-medium uppercase tracking-wide text-neutral-400">
            Paid out
          </div>
        </div>
      </div>

      {/* Filter pills (44px targets) */}
      <div className="mb-4 flex gap-2">
        {VIEWS.map((v) => (
          <button
            key={v.key}
            type="button"
            onClick={() => {
              setView(v.key);
              setPage(1);
            }}
            className={cn(
              'min-h-[44px] rounded-full px-5 text-sm font-medium transition-colors',
              view === v.key
                ? 'bg-white text-neutral-900'
                : 'bg-white/10 text-neutral-300 hover:bg-white/15',
            )}
          >
            {v.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-2.5">
          <SkeletonRow />
          <SkeletonRow />
        </div>
      ) : isError ? (
        <ErrorState onRetry={refetch} />
      ) : payouts.length === 0 ? (
        <EmptyState>
          {view === 'paid'
            ? 'Nothing paid out yet.'
            : view === 'owed'
              ? 'Nothing owed right now.'
              : 'No payouts yet — they appear here when a referral becomes a paying client.'}
        </EmptyState>
      ) : (
        <>
          {/* Mobile: cards */}
          <div className="space-y-2.5 md:hidden">
            {payouts.map((p) => (
              <div key={p.payoutId} className={`${TILE} px-4 py-3.5`}>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-white">{money(p.amount)}</span>
                  <StageBadge stage={p.status} />
                </div>
                <div className="mt-1 truncate text-sm text-neutral-300">{p.referredLabel}</div>
                <div className="mt-0.5 text-xs text-neutral-400">
                  {p.status === 'paid'
                    ? `Paid ${fmtDate(p.paidAt)}${p.paymentReference ? ` · ${p.paymentReference}` : ''}`
                    : `Earned ${fmtDate(p.eligibleAt)}`}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: table */}
          <div className={`${TILE} hidden overflow-hidden md:block`}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-xs font-medium uppercase tracking-wide text-neutral-400">
                  <th className="px-5 py-3">Referral</th>
                  <th className="px-5 py-3">Earned</th>
                  <th className="px-5 py-3">Paid</th>
                  <th className="px-5 py-3 text-right">Amount</th>
                  <th className="px-5 py-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.payoutId} className="border-b border-white/5 last:border-0">
                    <td className="px-5 py-3.5 font-medium text-white">{p.referredLabel}</td>
                    <td className="px-5 py-3.5 text-neutral-400">{fmtDate(p.eligibleAt)}</td>
                    <td className="px-5 py-3.5 text-neutral-400">
                      {p.paidAt ? (
                        <>
                          {fmtDate(p.paidAt)}
                          {p.paymentReference ? (
                            <span className="text-neutral-400"> · {p.paymentReference}</span>
                          ) : null}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-5 py-3.5 text-right font-semibold text-white">
                      {money(p.amount)}
                    </td>
                    <td className="px-5 py-3.5 text-center">
                      <StageBadge stage={p.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pager page={page} totalPages={totalPages} onPage={setPage} isFetching={isFetching} />
        </>
      )}
    </PortalLayout>
  );
};

export default PortalPayouts;
